import {
  CHARACTERS,
  JUMP_GRAVITY_SCALE,
  SLOWMO_RATE,
  TICK_RATE,
  THEMES,
  THEME_IDS,
  WEAPONS,
  WORLD_H,
  WORLD_W,
  carveCircle,
  createGame,
  drainEvents,
  effJumpPower,
  isSolid,
  simulateTrajectory,
  solveJump,
  solveShot,
  stepGame,
  WEAPON_IDS,
  type Entity,
  type GameState,
  type Intent,
  type PublicMatchConfig,
  type SimEvent,
  type ThemeId,
  type WeaponId,
} from '@cuboom/shared';
import { Camera } from './render/camera.js';
import { Renderer } from './render/renderer.js';
import { TerrainLayer } from './render/terrainLayer.js';
import { drawWater } from './render/water.js';
import { Gestures } from './input/gestures.js';
import { Toolbar, type Selection } from './ui/toolbar.js';
import { Hud } from './ui/hud.js';
import { WinScreen } from './ui/screens.js';
import { Particles } from './fx/particles.js';
import { Popups } from './fx/popups.js';
import { AudioEngine } from './audio/audioEngine.js';
import { CHAR_SCALE, drawCharacter } from './art/character.js';
import { drawProjectile, PROJ_COLORS } from './art/projectileArt.js';
import { drawProp } from './art/props.js';
import { Bubbles } from './art/bubbles.js';
import { Connection, type NetStatus } from './net/connection.js';
import { RemoteGame } from './game/remoteGame.js';
import { SimFork } from './game/fork.js';
import { Lobby, loadAimLine } from './ui/lobby.js';
import { enterFullscreenIfUseful } from './ui/fullscreen.js';
import { SQUAD_SIZE, cpuSquad, loadSquad } from './game/squad.js';
import { loadWeaponTweaks } from './game/tweaks.js';

// ---------------------------------------------------------------------------
// Full local turn loop against CPU, drag-to-aim + preview.
//   toolbar icons (bottom) → jump / weapons / swap / pass
//   tap                    → execute the pick, aimed at the cursor
//   drag                   → slingshot aim (weapons AND jump): pull back,
//                            release → it flies the other way. Swap drags
//                            onto an enemy.
//   mid-air (Boom Slingers)→ jump keeps the turn in aiming: hold a drag to
//                            crawl the world in slow-mo, release to shoot
//   stack                  → Jump + a weapon can both be armed: the jump
//                            takes the first drag, the fruit the mid-air one
//   W / Space              → jump to the mouse point (solver arc)
//   Enter                  → fire the selected weapon at the mouse point
//   1-9, Q/E               → select / cycle the 12-weapon arsenal · J/X modes
//   P                      → pass   ·   C = carve-tool toggle (debug)
//   R / T                  → new seed / cycle theme
//   MMB-drag / arrows      → pan · wheel = zoom · two fingers = pan + pinch
// Guides for all of this live in the menu (☰ → How to play), not on screen.
// ---------------------------------------------------------------------------

const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
const uiRoot = document.getElementById('ui-root') as HTMLElement;

const renderer = new Renderer(canvas);
const cam: Camera = renderer.cam;

/** The full 12-weapon arsenal (toolbar order + keyboard 1-9 / Q-E cycling). */
const ARSENAL: WeaponId[] = [...WEAPON_IDS];
const TURN_S = 30;

let themeIdx = 0;
let seed = Math.floor(Math.random() * 1e9);
let sel: Selection = { kind: 'weapon', weaponId: ARSENAL[0] };
/**
 * Jump stack slot: tapping Jump arms it WITHOUT disturbing the weapon
 * underneath, so the stack reads "jump + fruit". The armed jump wins the
 * next grounded drag/tap; it pops the moment the jump launches, leaving the
 * weapon still selected for the mid-air follow-up.
 */
let jumpArmed = false;
/**
 * Bullet-time hold: engaged by a mid-air drag-start, kept alive by a 2 s
 * heartbeat while the pointer is down, released on lift. The engine's
 * 90-tick expiry only exists as the safety net for a pointer we never saw
 * let go.
 */
let slowHeld = false;
let slowHeartbeat: number | null = null;
/**
 * Pigeon mark: tap an enemy (before firing) to lock the cruise missile on
 * them. Cleared whenever the selection changes or the shot is away.
 */
let lockTargetId: number | null = null;

function setSel(next: Selection): void {
  const changed =
    sel.kind !== next.kind ||
    (sel.kind === 'weapon' && next.kind === 'weapon' && sel.weaponId !== next.weaponId);
  if (changed) lockTargetId = null;
  sel = next;
}
let carveMode = false;
/** Transient jump/land face animations: entityId → { kind, expiry (seconds) }. */
const faceFx = new Map<number, { kind: 'jump' | 'land'; until: number }>();
/**
 * Body-animation timers, entityId → start seconds. takeoff = launch stretch,
 * land = touchdown squash; both decay 1 → 0 and are fed to drawCharacter.
 */
const takeoffFx = new Map<number, number>();
const landFx = new Map<number, number>();
/**
 * Round-begin cue, armed by the sim's turn_begin event: which entity the
 * marker belongs to and when it fired. drawTurnMarker uses it for the
 * arrow's pop/stab flourish and the ~1.4 s shockwaves after `born`.
 */
let turnCue = { id: -1, born: -10 };
let g: GameState;
let terrainLayer: TerrainLayer;
let lastMouseW = { x: WORLD_W / 2, y: WORLD_H / 3 };

/**
 * Offline runs the sim in this tab (`local`); online mirrors the server's
 * authoritative match through `remote`. Everything downstream (input, render,
 * juice) reads `g`, so the mode only decides who steps it and who owns input.
 */
let mode: 'local' | 'remote' = 'local';
/**
 * A match is live: the local sim keeps stepping and the menu collapses to its
 * two in-match options (Resume / Leave). Set by newGame and onMatchStart,
 * cleared when the match ends or the player leaves it for good.
 */
let matchLive = false;
let remote: RemoteGame | null = null;
/**
 * Full-local fork of OUR OWN online actions — see fork.ts. Every consequence
 * of our input (arcs, shells, sub-munitions, explosions, damage, craters)
 * simulates and plays here the same frame it happens; the server stream is
 * echo-confirmation that releases the fork only on divergence or turn end.
 */
const fork = new SimFork();
/**
 * What the screen and the input gates read while online: our fork of the
 * action when it lives, else the snapshot mirror. Offline they are the same
 * object (`g`), so every `sim()` read degrades to the plain local sim.
 */
function sim(): GameState {
  return fork.state ?? g;
}
let net: Connection | null = null;
let lobby: Lobby;
/** True while the current online match came from instant match, not a room code. */
let mmMatch = false;

// Juice — declared before newGame()/handleEvents() can touch them.
const fx = new Particles();
const popups = new Popups();
const audio = new AudioEngine();
const bubbles = new Bubbles();

function newGame(newSeed: number): void {
  mode = 'local';
  remote = null;
  fork.reset(); // never carry a fork across a match boundary
  matchLive = true;
  // Sync from the tap that launched this match: on a phone the browser still
  // counts it as a user gesture, so fullscreen can ride in before the fight.
  enterFullscreenIfUseful();
  seed = newSeed;
  endSlowDrag(); // never leak the heartbeat across matches
  jumpArmed = false;
  const themeId = THEME_IDS[themeIdx];
  const squad = loadSquad();
  g = createGame({
    seed,
    config: {
      theme: themeId,
      mode: 'teams',
      teamSize: SQUAD_SIZE,
      seats: 2,
      difficulty: 'normal',
      turnTimeMs: TURN_S * 1000,
      seed,
      // Menu-tuned balance: damage scaling, burn/acid/bee timers, jump power
      // and starting health — the same copy a hosted room is created from.
      weaponTweaks: loadWeaponTweaks(),
      // Aim-line assist is part of the match config so a hosted room can carry
      // the host's pick to every seat; offline it is simply your own setting.
      aimLine: loadAimLine(),
    },
    // Seat 1 is the CPU: the engine only runs decideAI for playerId === null.
    // Seat 0 carries the name typed in the menu's single name box — it is the
    // same name every online mode shows, so titles read "Rez wins!" offline too.
    roster: [
      { seat: 0, playerId: 'p1', name: Connection.storedName().trim() || 'P1', species: squad },
      { seat: 1, playerId: null, name: 'CPU', species: cpuSquad(squad) },
    ],
  });
  terrainLayer = new TerrainLayer(g.terrain, themeId);
  renderer.terrain = terrainLayer;
  fx.clear();
  popups.clear();
  bubbles.clear();
  winScreen.hide();
  cam.fitWorld(); // viewport is 1×1 only on the very first call; camera clamps, first frame re-fits
}

// --- UI --------------------------------------------------------------------

const hud = new Hud(uiRoot, TURN_S, () => openMenu(), {
  isOn: () => audio.isMusicOn,
  toggle: () => audio.toggleMusic(),
});

// A playmate dropped mid-match: tick their reconnect window down in the
// top-left corner. The server forfeits the match when it reaches zero — the
// same rule for invite rooms and matchmade ones. Cleared by that player's
// 'reconnected' event or by the match ending.
const dropBox = document.createElement('div');
dropBox.style.cssText =
  'position:absolute;left:8px;top:56px;color:#ffd76a;font:12px/1.55 monospace;' +
  'background:rgba(70,48,4,.85);padding:8px 10px;border-radius:8px;pointer-events:none;' +
  'white-space:pre-wrap;z-index:6;display:none;max-width:62vw;border:1px solid rgba(255,215,106,.55)';
uiRoot.appendChild(dropBox);
let dropTimer: number | null = null;
let dropWatchName = '';
/**
 * The playmate who quit on purpose ('left', not a network drop) during the
 * current match — read by onMatchEnd to award the win and by the end screen
 * to say who caused it. Cleared when a match starts or the match is left.
 */
let opponentLeftName = '';

function startDropWatch(name: string, seconds: number): void {
  if (dropTimer !== null) return; // already counting the earliest absence
  dropWatchName = name;
  const deadline = Date.now() + Math.max(1, seconds) * 1000;
  const tick = (): void => {
    const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    dropBox.textContent =
      left > 0
        ? `⏳ ${name} disconnected — ${left}s to reconnect\n    or the match is forfeited`
        : `⏳ ${name} never came back — the match is over`;
    dropBox.style.display = 'block';
  };
  tick();
  dropTimer = window.setInterval(tick, 500);
}

/** `who` narrows the clear to that player's watch (others may still be out). */
function stopDropWatch(who?: string): void {
  if (who !== undefined && dropWatchName !== '' && who !== dropWatchName) return;
  if (dropTimer !== null) {
    window.clearInterval(dropTimer);
    dropTimer = null;
  }
  dropWatchName = '';
  dropBox.style.display = 'none';
}

const toolbar = new Toolbar(uiRoot, ARSENAL, {
  onPick: (pick) => {
    audio.uiTap();
    if (pick.kind === 'pass') {
      jumpArmed = false;
      sendIntent('pass');
    } else if (pick.kind === 'jump') {
      jumpArmed = !jumpArmed; // tap again to unstack — the weapon keeps its slot
    } else if (pick.kind === 'swap') {
      jumpArmed = false; // swap and jump don't stack
      setSel(pick);
    } else {
      setSel(pick); // weapon — an armed jump stays stacked underneath
    }
  },
});

const winScreen = new WinScreen(uiRoot, {
  onRematch: () => {
    audio.uiTap();
    if (mode === 'remote') {
      winScreen.hide();
      // Instant match: this is a fresh search with whoever is queueing now
      // (humans only, so it may wait). Private room: "ready" starts the next
      // round.
      if (net?.isOnline) {
        // Find match: back onto the find-match screen with the search running,
        // so the queue (and its cancel button) is where the player can see it.
        if (mmMatch) lobby.startSearch();
        else net.setReady(true);
      }
      return;
    }
    newGame(Math.floor(Math.random() * 1e9));
  },
  onMenu: () => {
    matchLive = false;
    opponentLeftName = '';
    if (mode === 'remote') {
      // Done playing: leave the room cleanly so nothing drags us back into a
      // queued rematch (instant) or re-readies us (friend room).
      net?.leaveRoom();
      mode = 'local';
      remote = null;
      mmMatch = false;
    }
    winScreen.hide();
    openMenu(); // taps the ui sound itself
  },
  onOk: () => {
    // Friend room: the room is still there with both players in it. Back to
    // it so the host can re-tune map/ammo/power and both can ready up.
    audio.uiTap();
    matchLive = false;
    opponentLeftName = '';
    // Hide BEFORE requesting: onRoomState quiet-stores while the result
    // screen is up, so a reply that raced ahead would never open the room.
    winScreen.hide();
    lobby.openRoom(); // instant paint from the last stored room_state
    net?.requestState(); // fresh state — the server is already back in lobby
  },
});

// --- lobby + networking ----------------------------------------------------

/** Swap in the terrain the sim currently owns and follow it with the camera. */
function adoptTerrain(themeId: ThemeId): void {
  terrainLayer = new TerrainLayer(g.terrain, themeId);
  renderer.terrain = terrainLayer;
  fx.clear();
  popups.clear();
  bubbles.clear();
  cam.fitWorld();
}

function openMenu(): void {
  audio.uiTap();
  // Mid-match the menu is exactly Resume + Leave match (nothing else is
  // reachable while a game is running behind the overlay).
  lobby.showMenu(matchLive && g.phase !== 'match_end');
}

lobby = new Lobby(
  uiRoot,
  {
    onResume: () => lobby.hide(),
    onLeaveMatch: () => {
      audio.uiTap();
      // The match is over FOR GOOD — freeze a local game where it stands and
      // tell the server to forfeit an online one (it settles the result for
      // the others at once; the 30s grace is only for accidental drops).
      matchLive = false;
      opponentLeftName = '';
      if (mode === 'remote') {
        net?.leaveRoom();
        mode = 'local';
        remote = null;
        mmMatch = false;
      }
      stopDropWatch();
      winScreen.hide();
      lobby.showMenu(false); // we are already in the lobby: swap to the full menu
    },
    onPlayCpu: () => {
      lobby.hide();
      winScreen.hide();
      // Map pick applies to this match; 'random' rolls a fresh theme.
      const pick = lobby.chosenMap;
      themeIdx =
        pick === 'random'
          ? Math.floor(Math.random() * THEME_IDS.length)
          : Math.max(0, THEME_IDS.indexOf(pick));
      newGame(Math.floor(Math.random() * 1e9));
    },
    musicOn: () => audio.isMusicOn,
    onToggleMusic: () => audio.toggleMusic(),
    onOpenOnline: (name) => {
      lobby.clearError();
      Connection.storeName(name);
      connectOnline(name);
    },
    onFindMatch: (name) => {
      lobby.clearError();
      Connection.storeName(name);
      connectOnline(name);
      // Safe before the socket is up: Connection holds the search until the
      // server's `welcome` lands, then sends it. Strangers only — the server
      // never seats a CPU in a find-match game.
      net?.findMatch(Connection.storedName() || 'player');
    },
    onCancelMatch: () => {
      audio.uiTap();
      net?.cancelMatch();
    },
    onCreate: (config, code) => {
      audio.uiTap();
      if (!net?.isOnline) return lobby.showError('Not connected to the server yet.');
      net.createRoom(Connection.storedName() || 'player', config, code);
    },
    onJoin: (code) => {
      audio.uiTap();
      if (!net?.isOnline) return lobby.showError('Not connected to the server yet.');
      net.joinRoom(code, Connection.storedName() || 'player');
    },
    onReady: (ready) => {
      audio.uiTap();
      net?.setReady(ready);
    },
    onConfig: (patch) => {
      audio.uiTap();
      net?.setConfig(patch);
    },
    // The menu's single name box — typed once, stored as you type, used by
    // every mode (local seat name, find match, create/join, reconnects).
    onNameChange: (name) => Connection.storeName(name),
    onLeave: () => {
      audio.uiTap();
      net?.leaveRoom();
      // The match we were watching is gone — fall back to a fresh CPU game.
      mode = 'local';
      remote = null;
      matchLive = false; // never let the loop resume stepping a dead match
      opponentLeftName = '';
      winScreen.hide();
    },
  },
  Connection.storedName(),
);

/**
 * Where the WebSocket lives. Same-origin in dev (Vite proxies /ws); override
 * with `?server=host:port` when the client is served from somewhere else.
 */
function serverUrl(): string {
  // Explicit override: ?server=192.168.1.20:8787 (phone on the same Wi-Fi).
  const host = new URLSearchParams(location.search).get('server');
  if (host) return `ws://${host}`;
  // Otherwise the dev server's /ws proxy (see vite.config.ts), which keeps the
  // socket same-origin and survives https.
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.hostname || 'localhost'}${location.port ? `:${location.port}` : ''}/ws`;
}

/**
 * Ask the server what it offers. `GET /config` tells us whether instant match
 * is enabled; a server that doesn't answer (offline dev) just falls back to
 * the local defaults, so this never blocks the menu.
 */
async function loadServerInfo(): Promise<void> {
  try {
    const res = await fetch('/config', { headers: { accept: 'application/json' } });
    if (!res.ok) return;
    const info = (await res.json()) as { matchmaking?: boolean };
    lobby.setMatchmakingEnabled(info.matchmaking !== false);
  } catch {
    /* offline / not served by a master server — leave the default */
  }
}
void loadServerInfo();

function connectOnline(name: string): void {
  if (net?.isOnline) return;
  net = new Connection(serverUrl(), {
    onStatus: (status: NetStatus, detail) => {
      lobby.setOnline(status === 'online', detail);
      if (status === 'offline') lobby.showError('Connection lost — retrying…');
      else if (status === 'online') lobby.clearError();
    },
    onWelcome: (playerId) => lobby.setIdentity(playerId),
    onRoomState: (msg) => {
      mmMatch = msg.kind === 'instant';
      // A room update about a match in flight — or while the result screen is
      // already up — is bookkeeping only. Opening the room screen on top of
      // the match (or over the win screen) is never wanted; pre-match states
      // (create / join / ready-up) still surface it exactly as before. The
      // host sitting in Setup & tuning must not be yanked back to the room by
      // their own config echo or the friend's ready tap either.
      if (msg.inProgress || winScreen.visible || lobby.inRoomSetup) lobby.setRoomQuiet(msg);
      else lobby.setRoom(msg);
      lobby.setMatchmaking(null); // no longer searching: we have a room
      if (lobby.visible) audio.uiTap();
    },
    onMatchmaking: (msg) => {
      lobby.setMatchmaking(msg);
      if (lobby.visible && msg) audio.uiTap();
    },
    onMatchStart: (msg) => {
      stopDropWatch(); // a fresh match clears any stale countdown
      opponentLeftName = '';
      matchLive = true;
      // Best effort: the ready-tap usually already handled this on phones;
      // if no fresh gesture exists the request just declines silently.
      enterFullscreenIfUseful();
      remote = new RemoteGame(net!);
      remote.applyMatchStart(msg);
      fork.reset(); // a fresh match_start (incl. a resync) starts clean
      fork.setContext(msg.yourPlayerId);
      mode = 'remote';
      seed = msg.seed;
      themeIdx = Math.max(0, THEME_IDS.indexOf(msg.config.theme));
      g = remote.state!;
      adoptTerrain(msg.config.theme);
      winScreen.hide();
      lobby.hide();
    },
    onSnapshot: (msg) => {
      // Watchdog + clock beacon (or wholesale adopt on the first snapshot
      // after a match_start/resync) — snapshots never write the sim mid-match.
      remote?.applySnapshot(msg);
      // Reconcile our fork with the peer sim (may release it into the blend
      // when the peer moved past our turn).
      if (mode === 'remote' && remote?.state) fork.onSnapshot(remote.state);
    },
    onEvents: (events) => {
      // `sim_event` survives only as the reconnect carve replay
      // (sendResync: match_start → full carve log → snapshot): carve the
      // regenerated mask and redraw the layer. Normal play derives every
      // event locally from the lockstep sim — nothing is broadcast.
      if (mode !== 'remote') {
        handleEvents(events);
        return;
      }
      const live = remote?.applyEvents(events) ?? events;
      handleEvents(live);
    },
    onAck: (ok) => {
      // The ONE network verdict: routed point-to-point for an instant deny
      // sound, well before our own sim reaches the stamped copy (whose ack
      // the fork's FIFO pairing suppresses — one sound, ever).
      if (!ok) audio.deny();
    },
    onIntentApplied: (msg) => remote?.onIntentApplied(msg),
    onPong: (tick) => remote?.onPong(tick),
    onAimState: (msg) => remote?.applyAimState(msg),
    onMatchEnd: (winnerTeamId) => {
      const winner = winnerTeamId != null ? g.teams.find((t) => t.id === winnerTeamId) : null;
      const gone = dropWatchName; // read before the watch is cleared
      const quitBy = opponentLeftName;
      stopDropWatch();
      opponentLeftName = '';
      matchLive = false;
      // The result screen must be what the player sees — close an in-match
      // menu still sitting open (the remote sim keeps running behind it).
      if (lobby.visible) lobby.hide();
      // Quit-by-leave (explicit, or a grace that ran out): the others are
      // gone, so there is nobody to rematch — award the win and offer only
      // "Leave match". Only when it is OUR side that won, mind: in a bigger
      // match someone else leaving can still hand the win to a third seat.
      const left = quitBy || gone;
      const myTeamId = g.seats.find((s) => s.playerId === net?.selfId)?.teamId;
      // A friend room's result is always the single OK back to the room —
      // even here: the room survives the quit and the survivor can re-tune.
      const friendRoom = mode === 'remote' && !mmMatch;
      if (left && winnerTeamId != null && winnerTeamId === myTeamId) {
        winScreen.show(
          '🏆 You win!',
          `${left} left the match`,
          friendRoom ? { okOnly: true } : { leaveOnly: true },
        );
        audio.matchEnd(true); // forfeits carry no sim event — play the win here
        return;
      }
      const lane = mmMatch ? 'find match' : 'online';
      const again = !mmMatch ? 'ready up for a rematch' : 'find another match to search again';
      winScreen.show(
        winner ? `🏆 ${winnerLabel(winnerTeamId!)} wins!` : '🤝 Draw!',
        `${lane} · seed ${seed} · turn ${g.turnIndex}` +
          (mmMatch ? ` · ${again.charAt(0).toUpperCase()}${again.slice(1)}` : ''),
        // Rematch + Menu only — there is no New map anywhere on the result
        // screen (a friend room gets the single OK back to the room).
        { okOnly: friendRoom },
      );
    },
    onPlayerEvent: (kind, name, graceS) => {
      if (kind === 'left') {
        // Deliberate exit (drops say 'disconnected'). Remember who did it so
        // match_end can award the win and name them on the result screen.
        opponentLeftName = name;
        stopDropWatch(name);
      } else if (kind === 'disconnected') {
        startDropWatch(name, graceS ?? 30);
      } else if (kind === 'reconnected') {
        stopDropWatch(name);
      }
    },
    onError: (code, msg) => {
      lobby.showError(`${msg} (${code})`);
      audio.deny();
    },
  });
  net.connect(name);
}

newGame(seed);
lobby.showMenu(false);

// --- HUD + event feedback --------------------------------------------------

function handleEvents(evs: SimEvent[]): void {
  for (const ev of evs) {
    switch (ev.type) {
      case 'terrain_carved':
        terrainLayer.carveAll(ev.circles);
        break;
      case 'explosion': {
        trauma = Math.max(trauma, Math.min(1, ev.r / 130));
        fx.explosion(ev.x, ev.y, ev.r, PROJ_COLORS[ev.weaponId] ?? '#e8c24a');
        audio.explosion(ev.r);
        if (ev.r >= 90) popups.punch(ev.x, ev.y - ev.r * 0.5, 'BOOM!');
        break;
      }
      case 'damage':
        popups.damage(ev.x, ev.y - 28 * CHAR_SCALE, ev.amount);
        audio.splat();
        fx.burst('splat', ev.x, ev.y - 14 * CHAR_SCALE, {
          color: '#e04f3f',
          count: 7,
          speed: 130,
          jitter: 90,
          size: 3,
          life: 0.6,
        });
        break;
      case 'shake':
        trauma = Math.max(trauma, Math.min(1, ev.trauma));
        break;
      case 'splash':
        audio.splash();
        fx.burst('splash', ev.x, ev.y, {
          color: '#9fd8ff',
          count: 14,
          speed: 190,
          jitter: 130,
          size: 3.2,
          life: 0.7,
          angle: -Math.PI / 2,
          spread: 0.9,
        });
        // Ripple rings spreading from the impact point.
        fx.burst('ring', ev.x, ev.y, { color: '#ffffff', size: 52, life: 0.75, count: 2 });
        break;
      case 'prop_destroyed':
        fx.burst('leaf', ev.x, ev.y, {
          color:
            ev.kind === 'tree'
              ? '#3f9e54'
              : ev.kind === 'flower'
                ? '#e85d75'
                : ev.kind === 'lamp'
                  ? '#ffd76a'
                  : ev.kind === 'crystal'
                    ? '#c08cff'
                    : '#8b8f94',
          count: 9,
          speed: 90,
          jitter: 70,
          size: 3.4,
          life: 1.4,
        });
        break;
      case 'bounce':
        // Micro-bounces fire constantly — only poof the meaty ones.
        if (ev.speed > 140) {
          audio.bounce(ev.speed);
          fx.burst('dust', ev.x, ev.y, { color: '#d8d2c4', count: 4, speed: 55, jitter: 40, size: 3, life: 0.5 });
        }
        break;
      case 'projectile_spawn':
        audio.fire();
        // Beans volley: every pod pops out of the launcher with a powder
        // puff — the burst reads as an auto grenade launcher, not ten
        // silent dots appearing in the air.
        if (ev.projectile.weaponId === 'beans') {
          fx.burst('smoke', ev.projectile.x, ev.projectile.y, {
            color: '#cfc8b8',
            count: 3,
            speed: 30,
            jitter: 30,
            size: 2.4,
            life: 0.35,
          });
        }
        break;
      case 'voice': {
        const ve = g.entities.find((x) => x.id === ev.entityId);
        if (ve) audio.voice(CHARACTERS[ve.species], ev.kind);
        break;
      }
      case 'dialogue': {
        // Banter renders as a world-space bubble above the speaker.
        bubbles.show(ev.entityId, ev.text, ev.accent);
        // sayLine emits no voice event — blab here, mood by category.
        const de = g.entities.find((x) => x.id === ev.entityId);
        const mood =
          ev.category === 'hurt' || ev.category === 'low_hp'
            ? 'hurt'
            : ev.category === 'kill' || ev.category === 'celebrate' || ev.category === 'match_start'
              ? 'happy'
              : 'chirp';
        if (de) audio.voice(CHARACTERS[de.species], mood);
        break;
      }
      case 'turn_begin': {
        // The round-begin beat: arm the world marker (shockwaves + bouncing
        // arrow for ~1.4 s) and banner the new driver in team colors — a
        // plain white mark disappears against sand and snow.
        turnCue = { id: ev.entityId, born: performance.now() / 1000 };
        const te = g.entities.find((x) => x.id === ev.entityId);
        if (te) {
          // Key off the event's own entity, not g.activeEntityId: a remote
          // snapshot can still be carrying the previous turn at this point.
          const seat = g.seats.find((s) => s.seat === te.seat);
          const mine =
            seat?.playerId != null &&
            (mode !== 'remote' || seat.playerId === (net?.selfId ?? ''));
          popups.punch(
            te.x,
            te.y - 96 * CHAR_SCALE, // clears the name label and the arrow
            mine ? '▶ YOUR TURN' : `▶ ${te.name}`,
            g.teams[te.teamId].color,
            { fontPx: 34, life: 1.2, rise: -40 },
          );
        }
        break;
      }
      case 'intent_ack':
        // Offline: our own stepGame just denied an intent — same-frame sound.
        // Online: the sound already fired from the p2p ack (instant, before
        // the stamped copy applies), so local acks stay silent here — that
        // split is what keeps it to exactly one deny sound either way.
        if (!ev.ok && mode === 'local') audio.deny();
        break;
      case 'bee_spawn':
        audio.fire();
        break;
      case 'bee_sting':
        popups.damage(ev.x, ev.y - 20 * CHAR_SCALE, ev.amount);
        fx.burst('spark', ev.x, ev.y, {
          color: '#ffcd3c',
          count: 6,
          speed: 120,
          jitter: 80,
          size: 2.2,
          life: 0.4,
        });
        break;
      case 'jump_intent': {
        // Takeoff: wide eyes, launch stretch, a poof of dust, a "hup!".
        const nowT = performance.now() / 1000;
        faceFx.set(ev.entityId, { kind: 'jump', until: nowT + 0.55 });
        takeoffFx.set(ev.entityId, nowT);
        const je = g.entities.find((x) => x.id === ev.entityId);
        if (je && je.alive) {
          audio.voice(CHARACTERS[je.species], 'chirp');
          fx.burst('dust', je.x, je.y, {
            count: 9,
            speed: 95,
            jitter: 70,
            size: 3,
            life: 0.5,
            gravity: -40,
          });
          fx.burst('ring', je.x, je.y, { count: 1, size: 4, life: 0.25, color: 'rgba(255,255,255,.65)' });
        }
        break;
      }
      case 'land': {
        // Touchdown: squash, dust scaled by impact, a speed-pitched thud,
        // and a little screen kick on heavy landings. Fires for the bounce
        // pop AND the final stick — both are physical events worth showing.
        const nowL = performance.now() / 1000;
        landFx.set(ev.entityId, nowL);
        if (!g.entities.some((x) => x.id === ev.entityId && x.alive)) break;
        const pow = Math.min(1, ev.speed / 620);
        faceFx.set(ev.entityId, { kind: 'land', until: nowL + 0.45 });
        fx.burst('dust', ev.x, ev.y, {
          count: 4 + Math.round(pow * 9),
          speed: 45 + pow * 130,
          jitter: 60,
          size: 2.6 + pow * 2.4,
          life: 0.5,
        });
        audio.bounce(Math.min(ev.speed, 700));
        if (pow > 0.5) trauma = Math.min(1, trauma + pow * 0.16);
        break;
      }
      case 'death':
        audio.death();
        popups.punch(ev.x, ev.y - 46 * CHAR_SCALE, '☠', '#ff8d7a');
        fx.burst('leaf', ev.x, ev.y - 16 * CHAR_SCALE, {
          color: '#c9d66b',
          count: 10,
          speed: 110,
          jitter: 90,
          size: 3.6,
          life: 1.6,
        });
        break;
      case 'swap':
        audio.swap();
        for (const id of [ev.aId, ev.bId]) {
          const e = g.entities.find((x) => x.id === id);
          if (e) fx.burst('spark', e.x, e.y - 16 * CHAR_SCALE, { color: '#7ec8ff', count: 12, speed: 160, jitter: 100, size: 2.6, life: 0.55 });
        }
        break;
      case 'match_end': {
        // Online the `match_end` MESSAGE owns the result screen (it carries
        // stats and the forfeit/leave rules); our local sim still emits the
        // event at the same tick for determinism — don't let it clobber UI.
        if (mode === 'remote') break;
        const winner = ev.winnerTeamId != null ? g.teams.find((t) => t.id === ev.winnerTeamId) : null;
        matchLive = false; // over — the menu is the full list again
        // Remote broke out above, so this offline screen is never a friend
        // room's OK-only result — always Rematch + Menu.
        const friendRoom = false;
        audio.matchEnd(winner != null);
        winScreen.show(
          winner ? `🏆 ${winnerLabel(ev.winnerTeamId!)} wins!` : '🤝 Draw!',
          `seed ${seed} · ${THEME_IDS[themeIdx]} · turn ${g.turnIndex}`,
          // Same rule as the result screen above: Rematch + Menu, and a friend
          // room's one button is OK — back to the room both players are still in.
          { okOnly: friendRoom },
        );
        break;
      }
      default:
        break;
    }
  }
}

// --- intents ---------------------------------------------------------------

/**
 * Who just won, by the name players know them by: the seat name (real names
 * online, the menu name box / "CPU" offline) first, the team name only as a
 * fallback — results say "Rez wins!", not "Team 1 wins!".
 */
function winnerLabel(winnerTeamId: number): string {
  return (
    g.seats.find((s) => s.teamId === winnerTeamId)?.name ??
    g.teams.find((t) => t.id === winnerTeamId)?.name ??
    `Team ${winnerTeamId + 1}`
  );
}

function activeSeatPlayer(): string {
  const s = sim();
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  if (!e) return '';
  return s.seats.find((x) => x.seat === e.seat)?.playerId ?? '';
}

/**
 * Whose inventory belongs on this device's toolbar: our seat online, the
 * human seat offline (the hotseat's active human while one is up). The bar
 * counts THIS team's weapons even during a playmate's turn — their shelf is
 * theirs; ours only ever dims, never swaps out. Null = no seat here (bar
 * falls back to the active team). Named for its one job so it can't be
 * confused with the onMatchEnd local that reads the same seat online.
 */
function toolbarTeamId(): number | null {
  if (mode === 'remote') {
    const self = net?.selfId ?? '';
    return g.seats.find((s) => s.playerId === self)?.teamId ?? null;
  }
  const e = g.entities.find((x) => x.id === g.activeEntityId);
  const activeSeat = e ? g.seats.find((s) => s.seat === e.seat) : undefined;
  if (activeSeat?.playerId != null) return activeSeat.teamId; // human turn (hotseat)
  return g.seats.find((s) => s.playerId != null)?.teamId ?? null;
}

/**
 * True when the active character is driven from this device: our seat online,
 * any human seat in local mode (the hotseat shares one screen and one pointer).
 */
function activeIsMine(): boolean {
  const pid = activeSeatPlayer();
  if (mode === 'remote') return pid !== '' && pid === (net?.selfId ?? '');
  return pid !== '';
}

/** True while a human seat owns the active character (not the CPU's turn). */
function myTurn(): boolean {
  if (mode === 'remote') {
    // Read the FORK while it lives: between our submit and the first
    // post-action snapshot the mirror still claims the old phase/pose, and
    // every input gate must see exactly what offline would see this frame.
    const s = sim();
    const e = s.entities.find((x) => x.id === s.activeEntityId);
    if (!e || !e.alive) return false;
    const seat = s.seats.find((x) => x.seat === e.seat);
    return (
      s.phase === 'aiming' && seat != null && seat.playerId === (net?.selfId ?? '')
    );
  }
  const e = g.entities.find((x) => x.id === g.activeEntityId);
  if (!e) return false;
  const seat = g.seats.find((s) => s.seat === e.seat);
  return g.phase === 'aiming' && seat != null && seat.playerId != null;
}

/**
 * Mid-burst beans re-aim: while the volley handle is live the burst still
 * answers to the shooter's seat — myTurn() is false for the whole resolving
 * phase (and for a mid-air burst in aiming), so redirects get this gate.
 * Reads the fork's handle: our locally-steered burst is the one on screen.
 */
function volleyTurn(): boolean {
  const s = sim();
  if (!s.volley) return false;
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  if (!e) return false;
  const seat = s.seats.find((x) => x.seat === e.seat);
  if (!seat || seat.playerId == null) return false;
  if (mode === 'remote') return seat.playerId === (net?.selfId ?? '');
  return true;
}

/**
 * Grapes: while OUR cluster is airborne the tap means "burst it now" —
 * myTurn() is false for the whole resolving phase, so this gate (the
 * volleyTurn pattern) is what lets the tap through.
 */
function clusterTurn(): boolean {
  // The cluster counts from OUR fork too, not just the snapshot: our own
  // throw is on screen the moment the intent goes out, so a tap in the first
  // RTT window must reach the server exactly like offline does. (The fork
  // never exists offline, so the local branch is unaffected.)
  const s = sim();
  if (!s.projectiles.some((p) => p.behavior === 'split')) return false;
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  if (!e) return false;
  const seat = s.seats.find((x) => x.seat === e.seat);
  if (!seat || seat.playerId == null) return false;
  if (mode === 'remote') return seat.playerId === (net?.selfId ?? '');
  return true;
}

function sendIntent(kind: Intent['kind'], extra: Partial<Intent> = {}): void {
  const allowed =
    kind === 'aim' ? volleyTurn() : kind === 'split' ? clusterTurn() : myTurn();
  if (!allowed) return;
  if (mode === 'remote') {
    // The server owns turnIndex validation; it echoes an intent_ack either way.
    const playerId = net?.selfId ?? '';
    if (!playerId) return;
    // Mirror turnIndex on the wire — the server's timeline, never the fork's
    // (which may have locally begun the next chapter).
    const intent: Intent = { playerId, turnIndex: g.turnIndex, kind, ...extra };
    if (!remote?.submit(intent)) return;
    // Local first, wire second: run OUR action through the engine's own
    // stepGame inside the fork this very frame — offline parity, deny sound
    // included — then drain the fork's events straight into the FX pass. The
    // server's acks later pair FIFO against this local verdict: same verdict
    // = echo suppressed, disagreement on a position-bearing kind = release.
    fork.applyMine(intent, g);
    handleEvents(drainEvents(fork.state!));
    return;
  }
  const playerId = activeSeatPlayer();
  if (!playerId) return;
  const intent: Intent = { playerId, turnIndex: g.turnIndex, kind, ...extra };
  stepGame(g, intent);
  handleEvents(drainEvents(g));
}

let lastAimSentAt = 0;

/**
 * Stream our live aim to the other seats while we drive the active character:
 * the pointer's WORLD position and the weapon in hand — exactly what the local
 * renderer uses — throttled to ~50 ms. Display only: the server stamps our
 * playerId, relays it to the others, and the sim never sees it.
 */
function pushAimState(): void {
  if (mode !== 'remote' || !net?.isOnline) return;
  if (!myTurn() && !volleyTurn()) return;
  const now = performance.now();
  if (now - lastAimSentAt < 50) return;
  lastAimSentAt = now;
  net.send({
    t: 'aim_state',
    d: { x: lastMouseW.x, y: lastMouseW.y, weaponId: sel.kind === 'weapon' ? sel.weaponId : null },
  });
}

/**
 * Bullet time is drag-driven: engage the moment a drag starts mid-air on our
 * turn, keep the window alive with a heartbeat while the pointer is held, and
 * release on lift so normal speed resumes before the shot itself.
 */
function beginSlowDrag(): void {
  if (slowHeld) return;
  const s = sim();
  if (s.phase !== 'aiming' || !myTurn() || volleyTurn()) return;
  if (sel.kind !== 'weapon') return;
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  // Airborne test off the FORK's entity: between our submit and the server's
  // echo the mirror still claims "grounded" — the fork's arc is already up,
  // so bullet time engages the moment the drag starts, exactly as offline.
  if (!e || (e.onGround && !e.script)) return;
  slowHeld = true;
  sendIntent('slowmo', { slow: true });
  slowHeartbeat = window.setInterval(() => {
    // Re-arm only while still airborne — a landing mid-drag ends bullet time
    // for good (the engine clears the window on touchdown anyway).
    const f = sim();
    const a = f.entities.find((x) => x.id === f.activeEntityId);
    if (f.phase !== 'aiming' || !a || (a.onGround && !a.script) || !myTurn()) return;
    sendIntent('slowmo', { slow: true });
  }, 2000);
}

function endSlowDrag(): void {
  if (slowHeartbeat != null) {
    clearInterval(slowHeartbeat);
    slowHeartbeat = null;
  }
  if (!slowHeld) return;
  slowHeld = false;
  sendIntent('slowmo', { slow: false });
}

/** Launch the stacked jump (target or raw vector) and pop it off the stack. */
function sendJump(v: { target: { x: number; y: number } } | { vx: number; vy: number }): void {
  if (myTurn()) sendIntent('jump', v);
  jumpArmed = false;
}

function fireAt(wx: number, wy: number): void {
  const s = sim();
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  if (!e) return;
  const weaponId = sel.kind === 'weapon' ? sel.weaponId : ARSENAL[0];
  const def = WEAPONS[weaponId];
  const from = { x: e.x, y: e.y - 24 }; // the FORK's muzzle — mid-air drift and all
  const solved = solveShot(from, { x: wx, y: wy }, s.terrain, s.wind, {
    minSpeed: def.speed * 0.35,
    maxSpeed: def.speed,
    gravityScale: def.gravityScale,
    windScale: def.windScale,
  });
  // The engine aims along the target direction, so hand it the solved
  // velocity's endpoint (same trick the AI uses) and derive power from speed.
  const dir = solved ? { vx: solved.vx, vy: solved.vy } : { vx: wx - from.x, vy: wy - from.y };
  const len = Math.hypot(dir.vx, dir.vy) || 1;
  const speed = solved ? solved.speed : def.speed * 0.6;
  sendIntent('fire', {
    weaponId,
    target: { x: from.x + (dir.vx / len) * 200, y: from.y + (dir.vy / len) * 200 },
    power: Math.min(1, Math.max(0.1, speed / def.speed / 0.65 - 0.35 / 0.65)),
    // The pigeon chases whatever was tapped before firing.
    ...(def.behavior === 'homing' && lockTargetId != null ? { targetEntityId: lockTargetId } : {}),
  });
  if (def.behavior === 'homing') lockTargetId = null;
}

/** Nearest live enemy within reach of a world point (for swap targeting). */
function nearestEnemy(wx: number, wy: number): { id: number; x: number; y: number; d: number } | null {
  const s = sim();
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  if (!e) return null;
  let best: { id: number; x: number; y: number; d: number } | null = null;
  for (const o of s.entities) {
    if (!o.alive || o.teamId === e.teamId) continue;
    const d = Math.hypot(o.x - wx, o.y - wy);
    if (d < 52 && (!best || d < best.d)) best = { id: o.id, x: o.x, y: o.y, d };
  }
  return best;
}

/**
 * A tap on the field executes whatever the toolbar has selected — EXCEPT a
 * weapon: a tap never fires. Shooting is drag-aim-release only (`fireDrag`),
 * so an accidental brush of the screen can't burn a round.
 */
function actAt(wx: number, wy: number): void {
  const s = sim();
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  if (!e) return;
  // The stacked jump wins the tap while grounded — it is the first slot of
  // the stack, and the weapon below it fires on the NEXT action (mid-air).
  // `sim()` makes the airborne test exact mid-RTT: the fork's arc is already
  // flying, the mirror would still claim the ground.
  if (jumpArmed && e.onGround && !e.script) {
    // Tiny jump targets would burn the allowance for nothing.
    if (Math.hypot(wx - e.x, wy - e.y) < 24) return;
    sendJump({ target: { x: wx, y: wy } });
    return;
  }
  if (sel.kind === 'weapon') return; // tap ≠ fire — pull back and release instead
  if (sel.kind === 'swap') {
    const t = nearestEnemy(wx, wy);
    if (t) sendIntent('swap', { targetEntityId: t.id });
    return;
  }
  // Tiny jump targets would burn the allowance for nothing.
  if (Math.hypot(wx - e.x, wy - e.y) < 24) return;
  sendIntent(sel.kind, { target: { x: wx, y: wy } });
}

// --- aim preview (drag-to-aim) ---------------------------------------------

type AimPreview =
  | { kind: 'weapon'; points: { x: number; y: number }[]; power: number }
  | { kind: 'jump'; points: { x: number; y: number }[] }
  | { kind: 'swap'; tx: number; ty: number; ok: boolean };

let aimPreview: AimPreview | null = null;
let dragOx = 0;
let dragOy = 0;

/**
 * Cannon launch for slingshot jumps: the reversed pull IS the launch vector —
 * any angle you can pull (steep 60°, flat, straight up) leaves the barrel
 * exactly that way, and power ramps with pull length up to the character's
 * jump budget. No target re-solve: what you preview is what you fly.
 */
function jumpLaunchVec(
  e: GameState['entities'][number],
  dx: number,
  dy: number,
): { vx: number; vy: number } | null {
  const len = Math.hypot(dx, dy);
  if (len < 20) return null;
  const vmax = CHARACTERS[e.species].stats.jump * effJumpPower(g.config);
  const speed = vmax * (0.35 + 0.65 * Math.min(1, len / 240));
  return { vx: -(dx / len) * speed, vy: -(dy / len) * speed };
}

/** Preview arc for a raw launch vector — the exact flight the sim will run. */
function jumpVecPreview(
  e: GameState['entities'][number],
  v: { vx: number; vy: number },
): { x: number; y: number }[] {
  let fy = e.y;
  for (let lift = 0; lift < 8 && isSolid(g.terrain, e.x, fy); lift++) fy -= 1;
  const from = { x: e.x, y: fy - 1 };
  return simulateTrajectory(from.x, from.y, v.vx, v.vy, g.terrain, g.wind, {
    gravityScale: JUMP_GRAVITY_SCALE,
    windScale: 0.5,
    maxPoints: 70,
    step: 1 / 30,
    maxT: 6,
  }).points;
}

/** Preview arc for a tap/keyboard jump target — the engine's `solveJump`. */
function jumpPreviewPoints(
  to: { x: number; y: number },
  e: GameState['entities'][number],
): { x: number; y: number }[] {
  const vmax = CHARACTERS[e.species].stats.jump * effJumpPower(g.config);
  // Same pre-launch lift the engine does (a character stands *in* the top
  // solid row) — solve from the position the arc will actually start at.
  let fy = e.y;
  for (let lift = 0; lift < 8 && isSolid(g.terrain, e.x, fy); lift++) fy -= 1;
  const from = { x: e.x, y: fy - 1 };
  const sol = solveJump(from, to, g.terrain, g.wind, vmax);
  return simulateTrajectory(from.x, from.y, sol.vx, sol.vy, g.terrain, g.wind, {
    gravityScale: JUMP_GRAVITY_SCALE,
    windScale: 0.5,
    maxPoints: 60,
    step: 1 / 30,
    maxT: 5,
  }).points;
}

/** Preview arc for a weapon swipe (stops at first impact — that's the aim point). */
function weaponPreviewPoints(
  from: { x: number; y: number },
  dx: number,
  dy: number,
  power: number,
  weaponId: WeaponId,
): { x: number; y: number }[] {
  const def = WEAPONS[weaponId];
  const speed = def.speed * (0.35 + 0.65 * power);
  const len = Math.hypot(dx, dy) || 1;
  return simulateTrajectory(from.x, from.y, (dx / len) * speed, (dy / len) * speed, g.terrain, g.wind, {
    gravityScale: def.gravityScale,
    windScale: def.windScale,
    maxPoints: 55,
    step: 1 / 30,
    maxT: 6,
  }).points;
}

let lastVolleyAimAt = 0;

/**
 * Steer the LIVE beans burst to the arc this drag previews — reverse-drag
 * slingshot, same as firing, and the same vector the preview draws (power =
 * drag length), so what's on screen is exactly what the pods fly. Sent
 * throttled while a drag is HELD — pods leaving the muzzle mid-hold follow
 * the angle under the finger right now — and once more on release, which
 * locks that last angle in for every pod still in the magazine.
 */
function sendVolleyAim(dirx: number, diry: number): void {
  if (!volleyTurn()) return;
  const s = sim();
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  if (!e) return;
  const len = Math.hypot(dirx, diry);
  if (len < 6) return;
  const wid = s.volley?.weaponId ?? 'beans';
  const def = WEAPONS[wid];
  const speed = def.speed * (0.35 + 0.65 * Math.min(1, len / 240));
  const vx = (-dirx / len) * speed;
  const vy = (-diry / len) * speed;
  // Landing point of THAT arc — uncapped: the drawn preview array stops at
  // 55 points, which truncates a long flight mid-air (an aim point in the
  // sky would send the burst climbing for it).
  const end = simulateTrajectory(e.x, e.y - 24, vx, vy, s.terrain, s.wind, {
    gravityScale: def.gravityScale,
    windScale: def.windScale,
    maxT: 6,
  });
  sendIntent('aim', { target: { x: end.endX, y: end.endY }, vx, vy });
}

/** Fire along a swipe vector: slingshot feel — pull AWAY, the shot flies back. */
function fireDrag(dirx: number, diry: number): void {
  const s = sim();
  const e = s.entities.find((x) => x.id === s.activeEntityId);
  if (!e) return;
  const len = Math.hypot(dirx, diry);
  // Little pulls are real pulls. The preview already draws from 6px, so the
  // release has to fire too: power floors at the engine's 0.1 minimum, which
  // is exactly the "dragged a little → slow launch" end of the slingshot —
  // a full 240px pull reaches 1.0. (Gestures only routes real travel here —
  // taps ≤8px go to onTap, which never fires — and circling back to the
  // origin cancels.)
  if (len <= 8) return;

  // Mid-burst beans: releasing the drag locks the last steered angle in —
  // the held moves already walked the live pods, this pins the rest of the
  // magazine to the final one.
  if (volleyTurn()) {
    sendVolleyAim(dirx, diry);
    return;
  }

  const weaponId = sel.kind === 'weapon' ? sel.weaponId : ARSENAL[0];
  const def = WEAPONS[weaponId];
  const from = { x: e.x, y: e.y - 24 };
  sendIntent('fire', {
    weaponId,
    // Reverse the drag: you pull the sling back, the fruit goes the other way.
    target: { x: from.x - (dirx / len) * 200, y: from.y - (diry / len) * 200 },
    power: Math.min(1, Math.max(0.1, len / 240)),
    ...(def.behavior === 'homing' && lockTargetId != null ? { targetEntityId: lockTargetId } : {}),
  });
  if (def.behavior === 'homing') lockTargetId = null;
}

// --- gestures --------------------------------------------------------------

const dbg = { tap: 0, drag: 0, pan: 0, pinch: 0, wheel: 0 };
(window as unknown as { __cuboom: unknown }).__cuboom = { dbg, cam, get game() { return g; } };

const keys = new Set<string>();

canvas.addEventListener('pointermove', (e) => {
  const r = canvas.getBoundingClientRect();
  lastMouseW = cam.screenToWorld(e.clientX - r.left, e.clientY - r.top);
  pushAimState(); // one hook covers hover, drag, and keyboard aim alike
});

const gestures = new Gestures(canvas, {
  onPan: (dx, dy) => {
    dbg.pan++;
    cam.panScreen(dx, dy);
  },
  onPinch: (scale, cx, cy) => {
    dbg.pinch++;
    cam.zoomAt(cx, cy, scale);
  },
  onWheel: (deltaY, sx, sy) => {
    dbg.wheel++;
    cam.zoomAt(sx, sy, deltaY > 0 ? 0.9 : 1.1);
  },
  onDragStart: (sx, sy) => {
    dbg.drag++;
    dragOx = sx;
    dragOy = sy;
    aimPreview = null;
    // Touch down mid-air = grab the world by the scruff: bullet time opens
    // so the aim can be walked while everything crawls.
    beginSlowDrag();
  },
  onDragMove: (sx, sy) => {
    // Mid-burst beans: myTurn() is false while the burst flies, but the live
    // volley still answers to our drag — and a HELD drag steers it LIVE:
    // pods leaving the muzzle right now fly the arc under the finger
    // (reverse-drag slingshot, same as firing), throttled like pushAimState.
    // Release re-sends once to lock the final angle for the rest.
    if (volleyTurn()) {
      const s = sim();
      const e = s.entities.find((x) => x.id === s.activeEntityId);
      if (!e) {
        aimPreview = null;
        return;
      }
      const dx = sx - dragOx;
      const dy = sy - dragOy;
      const len = Math.hypot(dx, dy);
      if (len < 6) {
        aimPreview = null;
        return;
      }
      const wid = s.volley?.weaponId ?? 'beans';
      aimPreview = {
        kind: 'weapon',
        points: weaponPreviewPoints({ x: e.x, y: e.y - 24 }, -dx, -dy, Math.min(1, len / 240), wid),
        power: Math.min(1, len / 240),
      };
      const now = performance.now();
      if (now - lastVolleyAimAt >= 50) {
        lastVolleyAimAt = now;
        sendVolleyAim(dx, dy);
      }
      return;
    }
    if (!myTurn()) {
      aimPreview = null;
      return;
    }
    const mv = sim();
    const e = mv.entities.find((x) => x.id === mv.activeEntityId);
    if (!e) return;
    const dx = sx - dragOx;
    const dy = sy - dragOy;
    const len = Math.hypot(dx, dy);
    // Stacked jump wins the drag while grounded: pull back, fly the cannon —
    // the weapon underneath only takes over once the arc is in the air.
    // Reads the fork's body: mid-RTT the mirror is still at takeoff, so
    // without this a mid-air drag would re-preview a jump instead of the shot.
    if ((jumpArmed || sel.kind === 'jump') && e.onGround && !e.script) {
      if (len < 6) {
        aimPreview = null;
        return;
      }
      const v = jumpLaunchVec(e, dx, dy);
      aimPreview = v ? { kind: 'jump', points: jumpVecPreview(e, v) } : null;
      return;
    }
    if (sel.kind === 'weapon') {
      if (len < 6) {
        aimPreview = null;
        return;
      }
      aimPreview = {
        kind: 'weapon',
        // Slingshot preview: the arc leaves opposite the drag, like the shot.
        points: weaponPreviewPoints({ x: e.x, y: e.y - 24 }, -dx, -dy, Math.min(1, len / 240), sel.weaponId),
        power: Math.min(1, len / 240),
      };
    } else if (sel.kind === 'jump') {
      // Cannon preview: the reversed-pull launch vector flown exactly as the
      // sim will fly it — steep, flat, or straight up, whatever you pulled.
      const v = jumpLaunchVec(e, dx, dy);
      aimPreview = v ? { kind: 'jump', points: jumpVecPreview(e, v) } : null;
    } else if (sel.kind === 'swap') {
      const w = cam.screenToWorld(sx, sy);
      const t = nearestEnemy(w.x, w.y);
      aimPreview = t
        ? { kind: 'swap', tx: t.x, ty: t.y, ok: true }
        : { kind: 'swap', tx: w.x, ty: w.y, ok: false };
    } else {
      aimPreview = null;
    }
  },
  onDragEnd: (sx, sy, ox, oy, cancelled) => {
    // Bullet time ends the instant the pointer lifts — normal speed back
    // BEFORE the release action runs, so the shot itself plays at 1×.
    endSlowDrag();
    aimPreview = null;
    if (cancelled) return;
    // Mid-burst beans: release redirects the remaining pods — whatever the
    // toolbar has selected, the drag means "walk them over there".
    if (volleyTurn()) {
      fireDrag(sx - ox, sy - oy);
      return;
    }
    if (!myTurn()) return;
    const ed = sim();
    const e = ed.entities.find((x) => x.id === ed.activeEntityId);
    // Stacked jump release (grounded): the pull vector goes out raw — the
    // engine clamps the magnitude and flies the angle untouched. Too small
    // a drag is a tap, which falls through to actAt (jump-first as well).
    if (e && (jumpArmed || sel.kind === 'jump') && e.onGround && !e.script) {
      const v = jumpLaunchVec(e, sx - ox, sy - oy);
      if (v) {
        sendJump({ vx: v.vx, vy: v.vy });
        return;
      }
    } else if (sel.kind === 'weapon') {
      fireDrag(sx - ox, sy - oy);
      return;
    }
    const w = cam.screenToWorld(sx, sy);
    actAt(w.x, w.y); // tap-aim — jump | swap — reuses the guards
  },
  onTap: (sx, sy) => {
    dbg.tap++;
    const w = cam.screenToWorld(sx, sy);
    if (carveMode) {
      // Phase-3 terrain tool — punch the mask + the raster together.
      carveCircle(g.terrain, w.x, w.y, 45);
      terrainLayer.carve({ x: w.x, y: w.y, r: 47 });
      return;
    }
    // Grapes: one tap while OUR cluster is airborne bursts it here and now;
    // no tap and it lands whole for a single big bang. Claims the tap before
    // any weapon-select behavior (the toolbar may have another fruit armed).
    if (clusterTurn()) {
      audio.uiTap();
      sendIntent('split');
      return;
    }
    // Pigeon: tap an enemy to mark it — the missile homes in on the mark.
    // Tap again to unmark; the shot itself needs a drag. An armed jump
    // claims the tap first (the mark can wait until after takeoff).
    if (!jumpArmed && sel.kind === 'weapon' && WEAPONS[sel.weaponId].behavior === 'homing') {
      const t = nearestEnemy(w.x, w.y);
      if (t) {
        lockTargetId = lockTargetId === t.id ? null : t.id;
        audio.uiTap();
        return;
      }
    }
    actAt(w.x, w.y);
  },
});
// 1 finger = aim drag; camera stays on 2 fingers / MMB / wheel / arrows.
gestures.dragMode = 'aim';
(window as unknown as { __cuboom: { gestures: unknown } }).__cuboom.gestures = gestures;

// --- keyboard --------------------------------------------------------------

function cycleWeapon(dir: number): void {
  const cur = sel.kind === 'weapon' ? ARSENAL.indexOf(sel.weaponId) : -1;
  const base = cur < 0 ? (dir > 0 ? -1 : 0) : cur;
  const n = (base + dir + ARSENAL.length) % ARSENAL.length;
  setSel({ kind: 'weapon', weaponId: ARSENAL[n] });
}

window.addEventListener('keydown', (e) => {
  const k = e.key.toLowerCase();
  keys.add(k);
  if (e.repeat) return; // held keys only drive the camera-pan `keys` set above
  // Let a focused toolbar/menu button own space/enter (it blurs on click).
  if ((k === 'enter' || k === ' ') && document.activeElement instanceof HTMLButtonElement) return;
  if (k === 'r') newGame(Math.floor(Math.random() * 1e9));
  if (k === 't') {
    themeIdx = (themeIdx + 1) % THEME_IDS.length;
    newGame(seed); // same seed, new theme
  }
  if (k === 'c') {
    carveMode = !carveMode;
    console.log(carveMode ? 'carve ON — tap the terrain (C exits)' : 'carve off'); // debug key: console, never the screen
  }
  if (k === 'p') {
    jumpArmed = false;
    sendIntent('pass');
  }
  if (k === 'j') jumpArmed = !jumpArmed; // arms the stack slot, keeps the weapon
  if (k === 'x') {
    jumpArmed = false;
    setSel({ kind: 'swap' });
  }
  if (k === 'q') cycleWeapon(-1);
  if (k === 'e') cycleWeapon(1);
  if (k >= '1' && k <= '9') {
    const i = Number(k) - 1;
    if (i < ARSENAL.length) setSel({ kind: 'weapon', weaponId: ARSENAL[i] });
  }
  if (k === 'w' || k === ' ') {
    e.preventDefault();
    sendJump({ target: { x: lastMouseW.x, y: lastMouseW.y } });
  }
  if (k === 'enter') fireAt(lastMouseW.x, lastMouseW.y);
});
window.addEventListener('keyup', (e) => keys.delete(e.key.toLowerCase()));

function cameraPan(dt: number): void {
  const speed = 700 * dt;
  let dx = 0;
  let dy = 0;
  if (keys.has('arrowleft')) dx -= speed;
  if (keys.has('arrowright')) dx += speed;
  if (keys.has('arrowup')) dy -= speed;
  if (keys.has('arrowdown')) dy += speed;
  if (dx || dy) cam.panWorld(dx, dy);
}

// --- fixed-step sim + render loop ------------------------------------------

const TICK = 1 / TICK_RATE;
let acc = 0;
let trauma = 0;
let camFitted = false;

/**
 * Who's up: a floating arrow over the active character — no ring, the arrow
 * IS the marker. It idles with a bob and a soft squash-stretch, and when the
 * sim's turn_begin arms the cue it pops in and stabs downward (pointing AT
 * the new driver) while two shockwaves ring out. Team color + heavy dark
 * outline so it survives sand and snow alike.
 */
function drawTurnMarker(
  ctx: CanvasRenderingContext2D,
  e: Entity,
  color: string,
  nowS: number,
): void {
  const age = nowS - turnCue.born;
  const armed = turnCue.id === e.id && age >= 0;

  // Turn-flip shockwaves: two rings 0.16 s apart, each spreading 0.72 s.
  if (armed && age <= 1.4) {
    const cx = e.x;
    const cy = e.y - 15 * CHAR_SCALE; // chest of the scaled body
    for (let i = 0; i < 2; i++) {
      const a = age - i * 0.16;
      if (a < 0 || a > 0.72) continue;
      const k = a / 0.72;
      ctx.globalAlpha = (1 - k) * 0.85;
      ctx.beginPath();
      ctx.arc(cx, cy, (16 + k * 44) * CHAR_SCALE, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(0,0,0,0.55)';
      ctx.lineWidth = 6;
      ctx.stroke();
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  // The arrow: always on while this character holds the turn.
  //   pop   — easeOutBack over the first 0.3 s of a fresh turn (overshoot)
  //   stab  — dips toward the head through the first 0.6 s: it points at them
  //   bob/sq— idle float + squash-stretch, phase-shifted per character
  let pop = 1;
  let stab = 0;
  if (armed && age < 0.3) {
    const k = age / 0.3;
    const c1 = 1.70158;
    const c3 = c1 + 1;
    pop = 1 + c3 * (k - 1) ** 3 + c1 * (k - 1) ** 2;
  }
  if (armed && age < 0.6) stab = Math.sin((age / 0.6) * Math.PI) * 14;

  const ph = nowS * 3.4 + e.id;
  const bob = Math.sin(ph) * 5;
  const sq = Math.sin(ph) * 0.1;
  const hw = 15 * CHAR_SCALE;
  const hh = 13 * CHAR_SCALE;

  ctx.save();
  ctx.translate(e.x, e.y - 70 * CHAR_SCALE - hh / 2 + bob + stab);
  ctx.scale(pop * (1 - sq * 0.5), pop * (1 + sq));
  ctx.beginPath();
  ctx.moveTo(-hw, -hh / 2);
  ctx.lineTo(hw, -hh / 2);
  ctx.lineTo(0, hh / 2);
  ctx.closePath();
  ctx.strokeStyle = 'rgba(0,0,0,0.8)';
  ctx.lineWidth = 5;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
}

renderer.startLoop((dt) => {
  if (mode === 'local' && matchLive) {
    // Fixed 60 Hz sim, decoupled from render rate. Bullet-time (mid-air
    // shots) just feeds the accumulator more slowly — the sim still advances
    // exactly one tick per stepGame, so determinism is untouched.
    acc += g.slowmo ? dt / SLOWMO_RATE : dt;
    let guard = 0;
    while (acc >= TICK && guard++ < 10) {
      acc -= TICK;
      stepGame(g);
      handleEvents(drainEvents(g));
    }
  } else {
    // Lockstep peer: OUR copy of the sim steps here at 60 Hz (slowmo-rated,
    // steered to trail the server clock), applying stamped inputs at their
    // exact ticks — everyone's shots are simulated on this machine. Its
    // events run through the fork's FIFO filter first: what the fork already
    // played instantly (our own actions) is suppressed, everything else —
    // the opponent's whole turn — plays now, from local sim data.
    acc = 0;
    const now = performance.now();
    remote?.step(dt, now, (raw) => {
      const { live } = fork.filterEvents(raw, now);
      handleEvents(live);
    });
    // OUR fork: the instant mask for our own intents — steps at 60 Hz and
    // plays its events the same frame (offline parity); releases into a
    // 120 ms blend on divergence, turn-advance or cap, by which point the
    // peer sim's stamped copy has converged to the identical state.
    fork.stepFrame(dt, now, handleEvents);
    if (!fork.live()) fork.applyBlend(g, now);
  }

  cameraPan(dt);
  hud.update(sim());
  // The stack never outlives a turn — an armed jump from a round ago must
  // not glow (or fire) when the next one of ours rolls around.
  if (!myTurn()) jumpArmed = false;
  // Owned-turn gate: offline/local reads myTurn() (false on the CPU's turn),
  // online reads the remote seat — so the bar goes dark during the opponent's
  // turn in Find match and friend rooms, exactly as against the CPU. The
  // COUNTS stay ours throughout (toolbarTeamId): darkened, never someone
  // else's shelf.
  toolbar.sync(sim(), sel, jumpArmed, myTurn() || volleyTurn(), toolbarTeamId());
  fx.update(dt);
  popups.update(dt);
  trauma = Math.max(0, trauma - dt * 1.6);
  const shakeX = trauma * (Math.random() * 2 - 1) * 12;
  const shakeY = trauma * (Math.random() * 2 - 1) * 12;

  const themeId = THEME_IDS[themeIdx];
  // Shared clock for this frame's ambient motion (water waves, prop sway).
  const nowS = performance.now() / 1000;
  renderer.beginFrame(themeId, shakeX, shakeY);
  if (!camFitted) {
    // setView only happens inside beginFrame — fit once we know the viewport.
    camFitted = true;
    cam.fitWorld();
  }
  renderer.applyWorld();
  const ctx = renderer.ctx;

  renderer.drawTerrain();

  // What the field draws from: our fork while it lives (the instant local
  // view of our own actions), else the lockstep peer — our own copy of the
  // sim, ticking behind the server clock.
  const v = sim();

  // Water — animated waves, depth gradient, shore foam (render/water.ts).
  drawWater(ctx, v.water, THEMES[themeId], nowS);

  // Props — procedural art shared with the showcase page (sway/pulse alive).
  for (const p of v.props) {
    if (!p.alive) continue;
    drawProp(ctx, p, nowS);
  }

  // Characters: procedural species art, name, HP bar, turn marker.
  const nowMs = performance.now(); // aim-frame freshness reads milliseconds
  for (const e of v.entities) {
    const team = v.teams[e.teamId];
    const fxm = faceFx.get(e.id);
    if (fxm && fxm.until <= nowS) faceFx.delete(e.id);
    // One-shot jump pops: decay the takeoff stretch and landing squash toward
    // 0, then drop the entry so the next jump starts fresh.
    let takeoff = 0;
    const tStart = takeoffFx.get(e.id);
    if (tStart != null) {
      const k = 1 - (nowS - tStart) / 0.24;
      if (k <= 0) takeoffFx.delete(e.id);
      else takeoff = k;
    }
    let squash = 0;
    const lStart = landFx.get(e.id);
    if (lStart != null) {
      const k = 1 - (nowS - lStart) / 0.3;
      if (k <= 0) landFx.delete(e.id);
      else squash = k;
    }
    // Aim + held gear are per-owner now: our pointer when we drive, the
    // opponent's relayed frame (or the CPU's stashed decision) when they do,
    // and nothing at all for the rest of the field — the old code handed every
    // entity the LOCAL mouse, so the whole squad stared at you on your turn.
    const isActive = e.id === v.activeEntityId;
    const ownerIsMe = isActive && activeIsMine();
    let aim: { x: number; y: number } | null = null;
    let held: WeaponId | null = null;
    if (isActive) {
      if (ownerIsMe) {
        aim = lastMouseW;
        held = sel.kind === 'weapon' ? sel.weaponId : null;
      } else {
        const oppAim = remote?.aimFor(nowMs);
        const opp = oppAim && oppAim.playerId === activeSeatPlayer() ? oppAim : null;
        const cpu = v.cpuAim && v.cpuAim.entityId === e.id ? v.cpuAim : null;
        aim = opp
          ? { x: opp.x, y: opp.y }
          : cpu && cpu.x != null && cpu.y != null
            ? { x: cpu.x, y: cpu.y }
            : null;
        held = opp?.weaponId ?? cpu?.weaponId ?? null;
      }
    }
    drawCharacter(ctx, e, CHARACTERS[e.species], {
      active: isActive,
      scale: CHAR_SCALE,
      aim,
      t: nowS,
      held,
      mood: fxm && fxm.until > nowS ? fxm.kind : null,
      takeoff,
      squash,
    });

    if (isActive && v.phase !== 'match_end') {
      drawTurnMarker(ctx, e, team.color, nowS);
    }

    // HP number — read exact values at a glance (balance-friendly too).
    // Offsets and type ride CHAR_SCALE so the stack hugs the doubled body.
    const hpFrac = Math.max(0, e.hp / e.maxHp);
    const hpY = e.y - 42 * CHAR_SCALE;
    ctx.font = `bold ${13 * CHAR_SCALE}px system-ui`;
    ctx.textAlign = 'center';
    ctx.lineWidth = 3 * CHAR_SCALE;
    ctx.strokeStyle = 'rgba(0,0,0,0.75)';
    const hpTxt = e.alive ? `${Math.max(0, Math.ceil(e.hp))}` : '0';
    ctx.strokeText(hpTxt, e.x, hpY);
    ctx.fillStyle = !e.alive
      ? '#8a8a8a'
      : hpFrac > 0.5
        ? '#5ad85a'
        : hpFrac > 0.25
          ? '#e8c24a'
          : '#e04f3f';
    ctx.fillText(hpTxt, e.x, hpY);

    const nameY = e.y - 54 * CHAR_SCALE;
    ctx.font = `bold ${11 * CHAR_SCALE}px system-ui`;
    ctx.lineWidth = 3 * CHAR_SCALE;
    ctx.strokeStyle = 'rgba(0,0,0,0.7)';
    ctx.strokeText(e.alive ? e.name : `†${e.name}`, e.x, nameY);
    ctx.fillStyle = team.color;
    ctx.fillText(e.alive ? e.name : `†${e.name}`, e.x, nameY);
    ctx.globalAlpha = 1;
  }

  // Projectiles — per-weapon flight art, extracted to art/projectileArt.ts
  // so the art-showcase page draws the exact same art for review. One list,
  // one shell, one set of chili sparks: while the fork lives ITS list IS the
  // sim (our shot included, echoes already filtered), and when it releases
  // the mirror's list takes over — no suppression predicate needed anymore.
  for (const p of v.projectiles) {
    drawProjectile(ctx, p, { nowS, fx });
  }

  // Bees: little gold dots with a blur trail — the swarm is the weapon.
  for (const b of v.bees) {
    ctx.fillStyle = 'rgba(255, 205, 60, 0.35)';
    ctx.beginPath();
    ctx.arc(b.x - b.vx * 0.02, b.y - b.vy * 0.02, 3.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffcd3c';
    ctx.beginPath();
    ctx.arc(b.x, b.y, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(40,24,4,.9)';
    ctx.lineWidth = 1.4;
    ctx.stroke();
  }

  // Hazards: burning ground (molotov chili) and acid pools. Drawn over the
  // terrain — flames in front of anyone standing in them, as they should be.
  for (const h of v.hazards) {
    if (h.kind === 'fire') {
      const fade = h.turnsLeft <= 0 ? 0.75 : 1; // dying down on the last round
      const grd = ctx.createRadialGradient(h.x, h.y, 4, h.x, h.y, h.r);
      grd.addColorStop(0, `rgba(255,140,40,${0.34 * fade})`);
      grd.addColorStop(1, 'rgba(255,60,20,0)');
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.ellipse(h.x, h.y, h.r, h.r * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      const n = Math.max(5, Math.round(h.r / 11));
      for (let i = 0; i < n; i++) {
        const fx0 = h.x - h.r * 0.92 + ((i + 0.5) / n) * h.r * 1.84;
        const ph = nowS * 9 + i * 1.7;
        const fh = (11 + Math.sin(ph) * 5 + (i % 3) * 3.5) * fade;
        const w = 7.5 + Math.sin(ph * 1.3) * 2;
        ctx.fillStyle = `rgba(255,120,30,${0.85 * fade})`;
        ctx.beginPath();
        ctx.moveTo(fx0 - w / 2, h.y + 2);
        ctx.quadraticCurveTo(fx0 - w * 0.3, h.y - fh * 0.55, fx0, h.y - fh);
        ctx.quadraticCurveTo(fx0 + w * 0.3, h.y - fh * 0.55, fx0 + w / 2, h.y + 2);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = `rgba(255,225,120,${0.9 * fade})`;
        ctx.beginPath();
        ctx.moveTo(fx0 - w * 0.26, h.y + 1);
        ctx.quadraticCurveTo(fx0 - w * 0.12, h.y - fh * 0.5, fx0, h.y - fh * 0.62);
        ctx.quadraticCurveTo(fx0 + w * 0.12, h.y - fh * 0.5, fx0 + w * 0.26, h.y + 1);
        ctx.closePath();
        ctx.fill();
      }
      if (Math.random() < 0.35) {
        fx.burst('spark', h.x + (Math.random() - 0.5) * h.r * 1.6, h.y - 4, {
          color: '#ffb03a',
          count: 1,
          speed: 24,
          jitter: 34,
          size: 1.8,
          life: 0.7,
          gravity: -70,
        });
      }
      if (Math.random() < 0.14) {
        fx.burst('smoke', h.x + (Math.random() - 0.5) * h.r * 1.2, h.y - 8, {
          color: '#5d554e',
          count: 1,
          speed: 8,
          jitter: 18,
          size: 3,
          life: 1.1,
        });
      }
    } else if (h.kind === 'acid') {
      ctx.fillStyle = 'rgba(140,230,80,0.32)';
      ctx.beginPath();
      ctx.ellipse(h.x, h.y, h.r, h.r * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(190,255,120,0.55)';
      ctx.lineWidth = 1.6;
      ctx.stroke();
      for (let i = 0; i < 5; i++) {
        const b = (nowS * 0.8 + i * 0.2) % 1;
        const bx = h.x + Math.sin(i * 2.3 + nowS) * h.r * 0.6;
        const by = h.y - b * 11;
        ctx.strokeStyle = `rgba(190,255,130,${(1 - b) * 0.7})`;
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.arc(bx, by, 1.6 + b * 2.4, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  // Juice: particles under popups, both over characters/projectiles.
  fx.draw(ctx);
  popups.draw(ctx);
  bubbles.draw(ctx, (id) => {
    const e = v.entities.find((x) => x.id === id);
    return e ? { x: e.x, y: e.y } : null;
  });

  // Aim preview: dotted arc (weapon/jump) or destination marker (swap).
  if (aimPreview) {
    if (aimPreview.kind === 'swap') {
      const s = aimPreview;
      if (s.ok) {
        const me = v.entities.find((x) => x.id === v.activeEntityId);
        if (me) {
          ctx.strokeStyle = 'rgba(125,255,154,.8)';
          ctx.lineWidth = 2;
          ctx.setLineDash([8, 8]);
          ctx.beginPath();
          ctx.moveTo(me.x, me.y - 15 * CHAR_SCALE);
          ctx.lineTo(s.tx, s.ty - 15 * CHAR_SCALE);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        ctx.strokeStyle = 'rgba(125,255,154,.95)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(s.tx, s.ty - 15 * CHAR_SCALE, 22 * CHAR_SCALE, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.strokeStyle = 'rgba(255,107,107,.9)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(s.tx - 12, s.ty - 12);
        ctx.lineTo(s.tx + 12, s.ty + 12);
        ctx.moveTo(s.tx + 12, s.ty - 12);
        ctx.lineTo(s.tx - 12, s.ty + 12);
        ctx.stroke();
      }
    } else {
      const pts = aimPreview.points;
      const power = aimPreview.kind === 'weapon' ? aimPreview.power : 0.5;
      // `normal` = no assist: the weapon arc stops ~2/3 of the way to the
      // landing with no impact ring (jump previews always show the full path).
      // The match config wins — a host's pick is what everyone in the room
      // plays by — and storage is only the fallback for matches with no room.
      const full = aimPreview.kind !== 'weapon' || (g?.config.aimLine ?? loadAimLine()) === 'extended';
      const cut = full ? pts.length : Math.floor(pts.length * 0.62);
      for (let i = 4; i < cut; i += 4) {
        const t = i / pts.length;
        ctx.fillStyle =
          aimPreview.kind === 'weapon'
            ? `rgba(255, ${Math.round(210 - power * 130)}, 60, ${(0.35 + t * 0.6).toFixed(2)})`
            : `rgba(255,255,255,${(0.3 + t * 0.7).toFixed(2)})`;
        ctx.beginPath();
        ctx.arc(pts[i].x, pts[i].y, aimPreview.kind === 'weapon' ? 3 + power * 2 : 3.2, 0, Math.PI * 2);
        ctx.fill();
      }
      const last = full ? pts[pts.length - 1] : null;
      if (last) {
        ctx.strokeStyle = 'rgba(255,255,255,.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(last.x, last.y, 7, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  // Pigeon mark: red crosshair + sight line on the tapped target.
  if (lockTargetId != null) {
    const mark = v.entities.find((en) => en.id === lockTargetId && en.alive);
    if (!mark) {
      lockTargetId = null;
    } else {
      const shooter = v.entities.find((en) => en.id === v.activeEntityId);
      if (shooter) {
        ctx.strokeStyle = 'rgba(255,90,90,.55)';
        ctx.lineWidth = 1.6;
        ctx.setLineDash([7, 7]);
        ctx.beginPath();
        ctx.moveTo(shooter.x, shooter.y - 20 * CHAR_SCALE);
        ctx.lineTo(mark.x, mark.y - 16 * CHAR_SCALE);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      const spin = nowS * 2.4;
      ctx.strokeStyle = '#ff5a5a';
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.arc(mark.x, mark.y - 16 * CHAR_SCALE, 22 * CHAR_SCALE, spin, spin + Math.PI * 1.3);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(mark.x, mark.y - 16 * CHAR_SCALE, 22 * CHAR_SCALE, spin + Math.PI, spin + Math.PI * 1.3);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255,90,90,.9)';
      ctx.lineWidth = 2;
      for (const [dx, dy] of [[26, 0], [-26, 0], [0, 26], [0, -26]]) {
        ctx.beginPath();
        ctx.moveTo(
          mark.x + dx * 0.7 * CHAR_SCALE,
          mark.y - 16 * CHAR_SCALE + dy * 0.7 * CHAR_SCALE,
        );
        ctx.lineTo(mark.x + dx * CHAR_SCALE, mark.y - 16 * CHAR_SCALE + dy * CHAR_SCALE);
        ctx.stroke();
      }
      ctx.font = `bold ${11 * CHAR_SCALE}px system-ui`;
      ctx.textAlign = 'center';
      ctx.lineWidth = 3 * CHAR_SCALE;
      ctx.strokeStyle = 'rgba(0,0,0,.75)';
      ctx.strokeText('LOCKED', mark.x, mark.y - 46 * CHAR_SCALE);
      ctx.fillStyle = '#ff8d8d';
      ctx.fillText('LOCKED', mark.x, mark.y - 46 * CHAR_SCALE);
    }
  }

  // World border hint.
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = 4;
  ctx.strokeRect(0, 0, WORLD_W, WORLD_H);

  renderer.endFrame();
});

console.log('[cuboom] turn loop + drag-aim ready');
